/**
 * File Service Interface
 * 
 * This service abstracts file content fetching and management operations, following the
 * Dependency Inversion Principle. It can be easily mocked for testing
 * and extended for different file storage backends.
 */
class FileService {
  /**
   * Fetches file content by file ID
   * @param {string} fileId - The ID of the file to fetch
   * @returns {Promise<string>} Base64 encoded file content
   * @throws {Error} If file cannot be fetched
   */
  async getFileContent(fileId) {
    throw new Error('getFileContent must be implemented by concrete class');
  }

  /**
   * Fetches multiple file contents in parallel
   * @param {Array<string>} fileIds - Array of file IDs to fetch
   * @param {number} concurrencyLimit - Maximum concurrent requests
   * @returns {Promise<Map<string, string>>} Map of fileId to content
   */
  async getMultipleFileContents(fileIds, concurrencyLimit = 3) {
    const results = new Map();
    
    // Process in chunks to limit concurrency
    for (let i = 0; i < fileIds.length; i += concurrencyLimit) {
      const chunk = fileIds.slice(i, i + concurrencyLimit);
      const chunkPromises = chunk.map(async (fileId) => {
        try {
          const content = await this.getFileContent(fileId);
          return { fileId, content, success: true };
        } catch (error) {
          console.error(`Failed to fetch file ${fileId}:`, error);
          return { fileId, error: error.message, success: false };
        }
      });
      
      const chunkResults = await Promise.allSettled(chunkPromises);
      chunkResults.forEach((result) => {
        if (result.status === 'fulfilled' && result.value.success) {
          results.set(result.value.fileId, result.value.content);
        }
      });
    }
    
    return results;
  }

  /**
   * Adds a new file to the system
   * @param {object} fileData - File data object
   * @param {string} fileData.name - Original file name
   * @param {string} fileData.mimeType - File MIME type
   * @param {number} fileData.size - File size in bytes
   * @param {string} fileData.content - Base64 encoded file content
   * @param {object} fileData.metadata - Additional metadata (optional)
   * @returns {Promise<object>} Created file object with ID
   * @throws {Error} If file cannot be added
   */
  async addFile(fileData) {
    throw new Error('addFile must be implemented by concrete class');
  }

  /**
   * Removes a file from the system
   * @param {string} fileId - The ID of the file to remove
   * @returns {Promise<boolean>} True if file was successfully removed
   * @throws {Error} If file cannot be removed
   */
  async deleteFile(fileId) {
    throw new Error('removeFile must be implemented by concrete class');
  }

  /**
   * Updates file metadata
   * @param {string} fileId - The ID of the file to update
   * @param {object} updates - Object containing fields to update
   * @returns {Promise<object>} Updated file object
   * @throws {Error} If file cannot be updated
   */
  async updateFile(fileId, updates) {
    throw new Error('updateFile must be implemented by concrete class');
  }

  /**
   * Validates if a file ID is valid
   * @param {string} fileId - The file ID to validate
   * @returns {boolean} Whether the file ID is valid
   */
  isValidFileId(fileId) {
    return fileId && typeof fileId === 'string' && fileId.trim().length > 0;
  }

  /**
   * Validates file data for adding/updating
   * @param {object} fileData - File data to validate
   * @returns {object} Validation result with isValid boolean and errors array
   */
  validateFileData(fileData) {
    const errors = [];
    
    if (!fileData.name || typeof fileData.name !== 'string') {
      errors.push('File name is required and must be a string');
    }
    
    if (!fileData.mimeType || typeof fileData.mimeType !== 'string') {
      errors.push('MIME type is required and must be a string');
    }
    
    if (fileData.size !== undefined && (typeof fileData.size !== 'number' || fileData.size < 0)) {
      errors.push('File size must be a non-negative number');
    }
    
    if (!fileData.content || typeof fileData.content !== 'string') {
      errors.push('File content is required and must be a base64 string');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

/**
 * Concrete implementation using codexService
 */
export class CodexFileService extends FileService {
  constructor(codexService, codexId) {
    super();
    this.codexService = codexService;
    this.codexId = codexId;
  }

  async getFileContent(fileId) {
    if (!this.isValidFileId(fileId)) {
      throw new Error('Invalid file ID provided');
    }
    
    return await this.codexService.getFileContent(fileId);
  }

  async addFile(fileData) {
    const validation = this.validateFileData(fileData);
    if (!validation.isValid) {
      throw new Error(`Invalid file data: ${validation.errors.join(', ')}`);
    }
    if(!this.codexId) {
      throw new Error('Codex ID undefined');
    }
    
    const file = await this.codexService.addFile(fileData, this.codexId);

    return file;
    }

  async deleteFile(fileId) {
    if (!this.isValidFileId(fileId)) {
      throw new Error('Invalid file ID provided');
    }

    const response = await this.codexService.deleteFile(fileId);

    return response;
  }

  async updateFile(fileId, updates) {
    if (!this.isValidFileId(fileId)) {
      throw new Error('Invalid file ID provided');
    }
    
    // This will be implemented when the backend service is ready
    // For now, throw an error indicating the feature is not yet implemented
    throw new Error('File update functionality not yet implemented in backend service');
  }
}

/**
 * Mock implementation for testing
 */
export class MockFileService extends FileService {
  constructor(mockData = new Map()) {
    super();
    this.mockData = mockData;
    this.files = new Map(); // Store file metadata
    this.nextFileId = 1;
  }

  async getFileContent(fileId) {
    if (!this.isValidFileId(fileId)) {
      throw new Error('Invalid file ID provided');
    }
    
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 100));
    
    if (this.mockData.has(fileId)) {
      return this.mockData.get(fileId);
    }
    
    throw new Error(`File ${fileId} not found in mock data`);
  }

  async addFile(fileData) {
    const validation = this.validateFileData(fileData);
    if (!validation.isValid) {
      throw new Error(`Invalid file data: ${validation.errors.join(', ')}`);
    }
    
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const fileId = `mock-file-${this.nextFileId++}`;
    const file = {
      id: fileId,
      name: fileData.name,
      originalName: fileData.name,
      mimeType: fileData.mimeType,
      size: fileData.size || fileData.content.length,
      storagePath: `/mock/storage/${fileId}`,
      fileHash: `hash-${fileId}`,
      createdAt: new Date().toISOString(),
      createdBy: 'mock-user',
      updatedAt: new Date().toISOString(),
      updatedBy: 'mock-user'
    };
    
    this.files.set(fileId, file);
    this.mockData.set(fileId, fileData.content);
    
    return file;
  }

  async deleteFile(fileId) {
    if (!this.isValidFileId(fileId)) {
      throw new Error('Invalid file ID provided');
    }
    
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 150));
    
    if (!this.files.has(fileId)) {
      throw new Error(`File ${fileId} not found`);
    }
    
    this.files.delete(fileId);
    this.mockData.delete(fileId);
    
    return true;
  }

  async updateFile(fileId, updates) {
    if (!this.isValidFileId(fileId)) {
      throw new Error('Invalid file ID provided');
    }
    
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 100));
    
    if (!this.files.has(fileId)) {
      throw new Error(`File ${fileId} not found`);
    }
    
    const file = this.files.get(fileId);
    const updatedFile = { ...file, ...updates, updatedAt: new Date().toISOString() };
    this.files.set(fileId, updatedFile);
    
    return updatedFile;
  }

  setMockData(fileId, content) {
    this.mockData.set(fileId, content);
  }

  clearMockData() {
    this.mockData.clear();
    this.files.clear();
    this.nextFileId = 1;
  }

  getMockFiles() {
    return Array.from(this.files.values());
  }
}

/**
 * Factory function to create file services
 * @param {string} type - Service type ('codex' or 'mock')
 * @param {object} config - Configuration object
 * @returns {FileService} File service instance
 */
export const createFileService = (type, config = {}) => {
  switch (type) {
    case 'codex':
      return new CodexFileService(config.codexService, config.codexId);
    case 'mock':
      return new MockFileService(config.mockData);
    default:
      throw new Error(`Unknown file service type: ${type}`);
  }
};

export default FileService; 